<template>
  <div class="w-full flex flex-col">
    <div class="m-2 p-4 bg-white rounded shadow space-y-4 max-w-3xl">
      <p class="text-sm text-gray-600" v-if="loading">Loading course…</p>
      <p class="text-sm text-red-700" v-else-if="error">{{ error }}</p>
      <p class="text-sm text-gray-600" v-else-if="unavailable">This course is not published.</p>
      <template v-else-if="schema">
        <div class="flex items-start justify-between gap-3">
          <div>
            <p class="text-sm text-primary" v-if="isPreview">Preview</p>
            <h1 class="text-lg font-medium text-primary">{{ schema.meta.title }}</h1>
            <p class="text-sm text-gray-600" v-if="schema.meta.description">{{ schema.meta.description }}</p>
          </div>
          <p class="text-sm text-gray-600 whitespace-nowrap">Step {{ stepNumber }} of {{ steps.length }}</p>
        </div>
        <div class="h-2 bg-gray-100 rounded" role="progressbar" :aria-valuenow="percent" aria-valuemin="0" aria-valuemax="100">
          <div class="h-2 bg-primary rounded" :style="{ width: `${percent}%` }"></div>
        </div>
        <p class="text-xs text-gray-500">{{ percent }}% of required steps complete</p>
        <ol class="flex flex-wrap gap-2">
          <li v-for="(item, index) in steps" :key="item.id">
            <button class="px-2 py-1 text-sm border rounded disabled:text-gray-400" type="button" :disabled="!canOpen(item)" :aria-current="item.id === currentId ? 'step' : null" @click="go(item.id)">
              {{ index + 1 }}. {{ item.title }}
            </button>
          </li>
        </ol>

        <section class="space-y-4" v-if="step && !finished">
          <h2 class="text-base font-medium">{{ step.title }}</h2>
          <div class="space-y-3" v-for="(block, index) in step.blocks" :key="index">
            <p class="whitespace-pre-wrap" v-if="block.type === 'text'">{{ block.text }}</p>
            <p class="border-l-4 border-primary bg-gray-50 p-3 text-sm" v-else-if="block.type === 'callout'">{{ block.text }}</p>
            <ul class="list-disc pl-5 space-y-1" v-else-if="block.type === 'list'">
              <li v-for="item in block.items" :key="item">{{ item }}</li>
            </ul>
            <img class="max-w-full border rounded" v-else-if="block.type === 'image'" :src="block.url" :alt="block.alt || ''" />
            <iframe class="w-full aspect-video border rounded" v-else-if="block.type === 'video' && embedUrl(block.url)" :src="embedUrl(block.url)" :title="step.title"></iframe>
            <video class="w-full border rounded" v-else-if="block.type === 'video'" controls :src="block.url"></video>
            <details class="border rounded p-3" v-else-if="block.type === 'disclosure'">
              <summary class="cursor-pointer font-medium">{{ block.title || "More detail" }}</summary>
              <p class="mt-2 whitespace-pre-wrap">{{ block.text }}</p>
            </details>
          </div>

          <fieldset class="space-y-4" v-if="step.type === 'quiz' || step.type === 'caseStudy'">
            <legend class="sr-only">Questions</legend>
            <div class="space-y-2" v-for="question in questions" :key="question.id">
              <p class="font-medium">{{ question.prompt }}</p>
              <div class="flex flex-wrap gap-2">
                <label class="px-3 py-2 border rounded cursor-pointer" v-for="option in question.options" :key="option.id" :class="optionClass(question, option.id)">
                  <input class="mr-2" :type="question.kind === 'multi' ? 'checkbox' : 'radio'" :name="question.id" :checked="selected(question.id).includes(option.id)" @change="choose(question, option.id)" />
                  {{ option.label }}
                </label>
              </div>
              <p class="text-sm" v-if="revealed[step.id]" :class="questionCorrect(question) ? 'text-green-800' : 'text-red-700'">
                {{ questionCorrect(question) ? "Correct." : "Not correct." }} {{ question.explanation }}
              </p>
            </div>
          </fieldset>

          <div class="space-y-3" v-else-if="step.type === 'scenario'">
            <div class="flex flex-col gap-2">
              <button class="px-3 py-2 border rounded text-left" type="button" v-for="option in step.activity?.options || []" :key="option.id" :class="answer.optionId === option.id ? 'border-primary bg-gray-50' : ''" @click="answer.optionId = option.id">
                {{ option.label }}
              </button>
            </div>
            <p class="text-sm border-l-4 border-primary p-3" v-if="selectedOption">{{ selectedOption.consequence }}</p>
            <p class="text-sm" v-if="revealed[step.id] && step.activity?.explanation">{{ step.activity.explanation }}</p>
          </div>

          <div class="space-y-2" v-else-if="step.type === 'ordering'">
            <p>{{ step.activity?.prompt }}</p>
            <ol class="space-y-2">
              <li class="flex items-center gap-2" v-for="(itemId, index) in answer.order" :key="itemId">
                <span class="flex-grow border rounded px-3 py-2">{{ itemLabel(itemId) }}</span>
                <button class="px-2 py-1 border rounded" type="button" :disabled="index === 0" @click="move(index, -1)">Up</button>
                <button class="px-2 py-1 border rounded" type="button" :disabled="index === answer.order.length - 1" @click="move(index, 1)">Down</button>
              </li>
            </ol>
            <p class="text-sm" v-if="revealed[step.id]" :class="stepScore === 100 ? 'text-green-800' : 'text-red-700'">
              {{ stepScore === 100 ? "That order matches the procedure." : "That order does not match the procedure." }}
            </p>
          </div>

          <div class="space-y-3" v-else-if="step.type === 'hotspot'">
            <div class="relative border rounded" v-if="step.activity?.imageUrl">
              <img class="w-full" :src="step.activity.imageUrl" :alt="step.activity.alt || ''" />
              <button class="absolute w-6 h-6 -ml-3 -mt-3 rounded-full border border-primary bg-white text-xs" type="button" v-for="hotspot in step.activity.hotspots" :key="hotspot.id" :style="{ left: `${hotspot.x}%`, top: `${hotspot.y}%` }" :aria-label="hotspot.label" @click="answer.viewed[hotspot.id] = true; activeDetail = hotspot">
                {{ hotspot.label.slice(0, 1) }}
              </button>
            </div>
            <div class="flex flex-wrap gap-2" v-else>
              <button class="px-3 py-2 border rounded" type="button" v-for="hotspot in step.activity?.hotspots || []" :key="hotspot.id" @click="answer.viewed[hotspot.id] = true; activeDetail = hotspot">{{ hotspot.label }}</button>
            </div>
            <p class="text-sm border rounded p-3" v-if="activeDetail">{{ activeDetail.detail || activeDetail.label }}</p>
          </div>

          <div class="space-y-3" v-else-if="step.type === 'timeline'">
            <div class="flex flex-wrap gap-2">
              <button class="px-3 py-2 border rounded" type="button" v-for="stage in step.activity?.stages || []" :key="stage.id" @click="answer.viewed[stage.id] = true; activeDetail = stage">{{ stage.label }}</button>
            </div>
            <p class="text-sm border rounded p-3" v-if="activeDetail">{{ activeDetail.detail }}</p>
          </div>

          <label class="block space-y-2" v-else-if="step.type === 'shortAnswer'">
            <span class="font-medium">{{ step.activity?.prompt }}</span>
            <input class="w-full border rounded p-2" v-model="answer.text" @input="revealed[step.id] = false" />
            <p class="text-sm" v-if="revealed[step.id]" :class="stepScore === 100 ? 'text-green-800' : 'text-red-700'">
              {{ stepScore === 100 ? "That matches." : "That does not match the expected answer." }} {{ step.activity?.explanation }}
            </p>
          </label>

          <fieldset class="space-y-2" v-else-if="step.type === 'checklist'">
            <legend class="sr-only">Checklist</legend>
            <label class="flex items-center gap-2" v-for="item in step.activity?.items || []" :key="item.id">
              <input type="checkbox" v-model="answer.checked[item.id]" />
              <span>{{ item.label }}</span>
            </label>
          </fieldset>

          <p class="text-sm text-red-700" v-if="stepError">{{ stepError }}</p>
          <div class="flex gap-2">
            <button class="px-4 py-2 border rounded" type="button" :disabled="!previousId" @click="go(previousId)">Back</button>
            <button class="px-4 py-2 bg-primary text-white rounded" type="button" @click="continueStep">Continue</button>
          </div>
        </section>

        <section class="space-y-3" v-else-if="finished">
          <h2 class="text-base font-medium">{{ result.passed ? "Course complete" : "Course not yet passed" }}</h2>
          <p v-if="result.score != null">Score: {{ result.score }}%</p>
          <p class="text-sm" v-if="result.passed && !isPreview">This completion has been added to your staff training record.</p>
          <p class="text-sm" v-else-if="result.passed">Preview complete. A staff training record is written when a learner passes the published course.</p>
          <p class="text-sm" v-else>The pass mark was not met. You can retry the assessed steps.</p>
          <div class="flex gap-2">
            <button class="px-4 py-2 border rounded" type="button" v-if="!result.passed" @click="retry">Retry assessed steps</button>
            <router-link class="px-4 py-2 bg-primary text-white rounded" v-if="result.passed && !isPreview" :to="`/Matrices/Courses/${courseId}/certificate`">View completion record</router-link>
          </div>
        </section>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { auth, db } from "../../../db.js";
import {
  courseResult,
  nextStepId,
  restoreCourse,
  scoreAnswer,
} from "../../../../functions/functions-common/agents/agents/generateTrainingCourse/schema.mjs";
import { recordCourseCompletion } from "./courseApi.js";

const props = defineProps({
  schema: { type: Object, default: null },
  preview: { type: Boolean, default: false },
});

const route = useRoute();
const store = useStore();
const loading = ref(false);
const error = ref("");
const stepError = ref("");
const finished = ref(false);
const activeDetail = ref(null);
const courseRecord = ref(null);
const revealed = reactive({});
const progress = reactive({
  Started: "",
  CurrentStepId: "",
  Completed: {},
  Unlocked: {},
  Answers: {},
  StepScores: {},
  Score: null,
  Passed: false,
  RecordID: "",
  CompletedAt: "",
  Finished: false,
});

const isPreview = computed(() => props.preview || route.query.preview === "1");
const courseId = computed(() => courseRecord.value?.ID || route.params.id || "");
const schema = computed(() => restoreCourse(props.schema || courseRecord.value?.Schema));
const steps = computed(() => schema.value?.steps || []);
const step = computed(() => steps.value.find((item) => item.id === progress.CurrentStepId) || null);
const stepNumber = computed(() => Math.max(1, steps.value.findIndex((item) => item.id === progress.CurrentStepId) + 1));
const requiredSteps = computed(() => steps.value.filter((item) => item.required !== false));
const percent = computed(() => {
  if (!requiredSteps.value.length) return 0;
  const done = requiredSteps.value.filter((item) => progress.Completed[item.id]).length;
  return Math.round((done / requiredSteps.value.length) * 100);
});
const currentId = computed(() => progress.CurrentStepId);
const unavailable = computed(() => !props.schema && courseRecord.value && courseRecord.value.Status !== "published" && !isPreview.value);
const answer = computed(() => (step.value && progress.Answers[step.value.id]) || {});
const questions = computed(() => step.value?.activity?.questions || []);
const selectedOption = computed(() => (step.value?.activity?.options || []).find((option) => option.id === answer.value.optionId) || null);
const stepScore = computed(() => progress.StepScores[step.value?.id]);
const result = computed(() => courseResult(schema.value, progress));
const previousId = computed(() => {
  const index = steps.value.findIndex((item) => item.id === progress.CurrentStepId);
  for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
    if (progress.Unlocked[steps.value[cursor].id]) return steps.value[cursor].id;
  }
  return "";
});

function userId() {
  return store.getters.userID && store.getters.userID !== "Unknown"
    ? store.getters.userID
    : auth.currentUser?.uid || "";
}

function shuffle(items, seed) {
  const copy = items.slice();
  let state = String(seed || "course").split("").reduce((sum, char) => sum + char.charCodeAt(0), 1);
  for (let index = copy.length - 1; index > 0; index -= 1) {
    state = (state * 16807) % 2147483647;
    const swap = state % (index + 1);
    [copy[index], copy[swap]] = [copy[swap], copy[index]];
  }
  return copy;
}

function ensureAnswer(item) {
  if (!progress.Answers[item.id]) {
    if (item.type === "quiz" || item.type === "caseStudy") progress.Answers[item.id] = { questions: {} };
    else if (item.type === "shortAnswer") progress.Answers[item.id] = { text: "" };
    else if (item.type === "ordering") {
      const ids = (item.activity?.items || []).map((entry) => entry.id);
      progress.Answers[item.id] = { order: shuffle(ids, item.id) };
    } else if (item.type === "scenario") progress.Answers[item.id] = { optionId: "" };
    else if (item.type === "checklist") progress.Answers[item.id] = { checked: {} };
    else progress.Answers[item.id] = { viewed: {} };
  }
  return progress.Answers[item.id];
}

function canOpen(item) {
  if (!progress.Unlocked[item.id]) return false;
  if (schema.value?.meta?.freeNavigation) return true;
  return item.id === progress.CurrentStepId || progress.Completed[item.id] === true;
}

function go(id) {
  const item = steps.value.find((entry) => entry.id === id);
  if (!item || !canOpen(item)) return;
  progress.CurrentStepId = id;
  finished.value = false;
  activeDetail.value = null;
  stepError.value = "";
}

function selected(questionId) {
  const values = answer.value.questions?.[questionId];
  return Array.isArray(values) ? values : [];
}

function choose(question, optionId) {
  revealed[step.value.id] = false;
  const current = selected(question.id).slice();
  if (question.kind === "multi") {
    const index = current.indexOf(optionId);
    if (index >= 0) current.splice(index, 1);
    else current.push(optionId);
  } else {
    current.splice(0, current.length, optionId);
  }
  answer.value.questions[question.id] = current;
}

function questionCorrect(question) {
  const given = selected(question.id).slice().sort();
  const expected = (question.correctOptionIds || []).slice().sort();
  return given.length === expected.length && given.every((id, index) => id === expected[index]);
}

function optionClass(question, optionId) {
  if (!revealed[step.value?.id]) return selected(question.id).includes(optionId) ? "border-primary" : "";
  if ((question.correctOptionIds || []).includes(optionId)) return "border-green-700 bg-green-50";
  if (selected(question.id).includes(optionId)) return "border-red-700 bg-red-50";
  return "";
}

function itemLabel(itemId) {
  return (step.value?.activity?.items || []).find((item) => item.id === itemId)?.label || itemId;
}

function move(index, direction) {
  const order = answer.value.order.slice();
  const target = index + direction;
  if (target < 0 || target >= order.length) return;
  const [item] = order.splice(index, 1);
  order.splice(target, 0, item);
  answer.value.order = order;
  revealed[step.value.id] = false;
}

function embedUrl(url) {
  const watch = String(url || "").match(/[?&]v=([^&]+)/);
  if (watch) return `https://www.youtube.com/embed/${watch[1]}`;
  const short = String(url || "").match(/youtu\.be\/([^?]+)/);
  if (short) return `https://www.youtube.com/embed/${short[1]}`;
  return "";
}

function progressPath() {
  return `Userdata/${userId()}/Training/CourseProgress/${courseId.value}`;
}

async function persist() {
  if (isPreview.value || props.schema || !userId() || !courseId.value) return;
  await db.set(progressPath(), JSON.parse(JSON.stringify(progress)));
}

async function continueStep() {
  if (!step.value) return;
  stepError.value = "";
  const scored = scoreAnswer(step.value, answer.value);
  if (!scored.complete) {
    stepError.value = "Complete this step before continuing.";
    return;
  }
  if (scored.scored && !revealed[step.value.id]) {
    revealed[step.value.id] = true;
    progress.StepScores[step.value.id] = scored.score;
    return;
  }
  progress.Completed[step.value.id] = true;
  if (scored.scored && typeof scored.score === "number") progress.StepScores[step.value.id] = scored.score;
  const next = nextStepId(schema.value, step.value, { ...scored, answer: answer.value });
  if (next) {
    progress.Unlocked[next] = true;
    progress.CurrentStepId = next;
    activeDetail.value = null;
    await persist();
    return;
  }
  finished.value = true;
  progress.Finished = true;
  const outcome = courseResult(schema.value, progress);
  progress.Score = outcome.score;
  progress.Passed = outcome.passed;
  if (outcome.passed && !isPreview.value && userId()) {
    try {
      const saved = await recordCourseCompletion(db, {
        course: { ...courseRecord.value, ID: courseId.value },
        schema: schema.value,
        progress,
        userId: userId(),
      });
      progress.RecordID = saved.recordId || progress.RecordID;
      progress.CompletedAt = saved.completedAt || progress.CompletedAt;
    } catch (err) {
      error.value = err?.message || "The training record could not be saved.";
    }
  }
  await persist();
}

function retry() {
  for (const item of steps.value) {
    const assessed = ["quiz", "shortAnswer", "ordering", "caseStudy"].includes(item.type)
      || (item.type === "scenario" && (item.activity?.correctOptionIds || []).length > 0);
    if (!assessed) continue;
    delete progress.Completed[item.id];
    delete progress.StepScores[item.id];
    delete progress.Answers[item.id];
    revealed[item.id] = false;
  }
  const first = steps.value.find((item) => ["quiz", "shortAnswer", "ordering", "caseStudy", "scenario"].includes(item.type)) || steps.value[0];
  progress.CurrentStepId = first?.id || "";
  progress.Passed = false;
  progress.Finished = false;
  finished.value = false;
}

function applySchema(value) {
  const restored = restoreCourse(value);
  if (!restored?.steps?.length) return;
  if (!progress.CurrentStepId || !restored.steps.some((item) => item.id === progress.CurrentStepId)) {
    progress.CurrentStepId = restored.steps[0].id;
  }
  if (!progress.Started) progress.Started = new Date().toISOString();
  progress.Unlocked[restored.steps[0].id] = true;
  if (progress.Finished || (progress.Passed && progress.RecordID)) finished.value = true;
}

async function load() {
  if (props.schema) {
    applySchema(props.schema);
    return;
  }
  loading.value = true;
  error.value = "";
  try {
    await db.watch("Home/Courses", store);
    const id = route.params.id;
    const record = store.state["Home/Courses"]?.[id];
    if (!record) {
      error.value = "This course was not found.";
      return;
    }
    courseRecord.value = { ...record, ID: record.ID || id };
    if (userId() && !isPreview.value) {
      const snapshot = await db.ref(progressPath()).once("value");
      const saved = snapshot.val();
      if (saved) Object.assign(progress, saved);
    }
    applySchema(courseRecord.value.Schema);
  } catch (err) {
    error.value = err?.message || "The course could not be opened.";
  } finally {
    loading.value = false;
  }
}

watch(step, (item) => {
  if (item) ensureAnswer(item);
}, { immediate: true });

watch(() => props.schema, (value) => {
  if (!value) return;
  progress.CurrentStepId = "";
  progress.Completed = {};
  progress.Unlocked = {};
  progress.Answers = {};
  progress.StepScores = {};
  progress.Finished = false;
  progress.Passed = false;
  finished.value = false;
  applySchema(value);
});

onMounted(load);
</script>
